import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import dayjs from "dayjs";
import { Select, Spin, Tooltip, TreeSelect, message } from "antd";
import { FaTrashAlt } from "react-icons/fa";
import { PiExportBold } from "react-icons/pi";
import { LoadingOutlined } from "@ant-design/icons";

import "./styles.scss"
import { searchByOptions } from "../esg-chat";
import Breadcrumbs from "../../../assets/component/breadcrumbs";
import ManageFilters from "../../../assets/component/manage-filters";
import UnSubscribeUI from "../../../assets/component/page-errors/un-subscribe";
import PDFPreview from "../../../assets/component/pdf-preview";
import CustomTable from "../../../assets/component/table";
import { getParentAndChildrenOptions } from "../../../helpers/array-format";
import { decryptData } from "../../../helpers/encrypt/crypto-encrypt";
import { ESGChatFoldersAction } from "../../../store/slices/ai-chat/esgChat";
import { DeleteSourceAction, DeleteSourceReset, ExportSourceAction, ExportSourceListReset, SourceListAction, SourceListReset } from "../../../store/slices/ai-chat/sourceList";
import { FetchCompaniesAction, FetchCompaniesReset } from "../../../store/slices/ai-chat/fetchCompanies";
import { S3URLRequestAction, S3URLRequestReset } from "../../../store/slices/s3urlRequest";
import CustomModal from "../../../assets/component/modal";
import SpinLoader from "../../../assets/component/loader/loader";
import { RetrievePortfolioData, retrievePortfolioReset } from "../../../store/slices/portfolio/retrievePortfolio";

export const tableHeaderData = [
    {
        title: 'Disclosure Type',
        dataIndex: 'sourceTypeName',
        key: 'sourceTypeName',
        sorter: {
            compare: (a, b) => a.sourceTypeName.localeCompare(b.sourceTypeName),
            multiple: 3,
        },
    },
    {
        title: 'Title',
        dataIndex: 'title',
        key: 'title',
        sorter: {
            compare: (a, b) => a.title.localeCompare(b.title),
            multiple: 2,
        },
    },
    {
        title: 'Publication Date',
        dataIndex: 'publicationDate',
        key: 'publicationDate',
        width: 150,
        sorter: {
            compare: (a, b) => new Date(a.publicationDate) - new Date(b.publicationDate),
            multiple: 1,
        },
    },
    {
        title: 'Source',
        dataIndex: 'sourceUrl',
        key: 'sourceUrl',
    },
    {
        title: '',
        dataIndex: 'actions',
        key: 'actions',
    },
];

const SourceListMain = ({ model, unsubscribed }) => {
    const [selectdCompany, setSelectedCompany] = useState(null)
    const [selectedPublicationYear, setSelectedPublicationYear] = useState(null)
    const [selectdSource, setSelectedSource] = useState([])
    const [selectedSourceDetails, setSelectedSourceDetails] = useState({ parents: [], children: [] })
    const [sourceOptions, setSourceOptions] = useState([])
    const [fiscalYearOptions, setFiscalYearOptions] = useState([])
    const [showPDFPreview, setShowPDFPreview] = useState(false)
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(50)
    const [showFilters, setShowFilters] = useState(true)
    const [showDeleteSourcePop, setShowDeleteSourcePop] = useState(false);
    const [selectedSourceFile, setSelectedSourceFile] = useState(null);
    const [formattedTableData, setFormattedTableData] = useState({
        dataSource: [],
        columns: []
    })
    const [searchCompany, setSearchCompany] = useState('')
    const [companiesOptions, setCompaniesOptions] = useState([]);

    const dispatch = useDispatch()
    const debounceTimeout = useRef(null);
    const selectedFiltersData = searchByOptions?.find(item => item.value === model)
    const { data: companyList, isLoading: companyLoading } = useSelector((state) => state.fetchCompanies)
    const { data: chatFolders, isLoading: chatLoading } = useSelector((state) => state.chatFolders)
    const { data: sourceList, isLoading: sourceListLoading, error: sourceListError } = useSelector((state) => state.sourceList)
    const { data: deleteSourceData, isLoading: deleteSourceLoading, error: deleteSourceError, success: deleteSourceSuccess } = useSelector((state) => state.deleteSource)
    const { success: exportSourceSuccess, error: exportSourceError, isLoading: exportSourceLoading } = useSelector((state) => state.exportSourceList);
    const { data: userPortfolioData, isLoading: userPortfolioDataLoading } = useSelector((state) => state?.retrievePortfolio) || {};

    const defaultOption = { title: "All", value: "all" };
    const role_decrypt = decryptData(sessionStorage.getItem('eqlwoer'))

    const onChangeCompany = (e) => {
        setSelectedCompany(e)
        setSelectedPublicationYear(null)
    }

    const onChangePublicationYear = (e) => {
        setSelectedPublicationYear(e)
    }

    const onChangeSource = (values) => {
        if (values?.includes(defaultOption?.value)) {
            setSelectedSource([defaultOption?.value])
        } else {
            if (values?.length > 0) {
                if (values?.length <= 5) {
                    const sourceParentChildDetails = getParentAndChildrenOptions({ data: sourceOptions, selectedSource: values });

                    setSelectedSourceDetails(sourceParentChildDetails)
                    setSelectedSource(values)
                } else {
                    message.error("Maximum limit exceeded!")
                }
            } else {
                setSelectedSource([])
                setSelectedSourceDetails({ parents: [], children: [] })
            }
        }
    }

    const onReset = () => {
        dispatch(SourceListReset())
        dispatch(S3URLRequestReset())
        setSelectedSource([])
        setSelectedCompany(null)
        setSelectedPublicationYear(null)
        setSelectedSourceDetails({ parents: [], children: [] })
    }

    const handlePreviewPDF = async (url) => {
        dispatch(S3URLRequestReset())
        dispatch(S3URLRequestAction(url))
        setShowPDFPreview(true)
    }

    const handleCompanySearch = (value) => {
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        if (value?.length > 2) {
             debounceTimeout.current = setTimeout(() => {
                setSearchCompany(value);
             }, 500);
        }
    }

    const handleDeleteSourceModal = (source) => {
        setShowDeleteSourcePop(true);
        setSelectedSourceFile(source);
    }

    const handleCloseDeleteModal = () => {
        setShowDeleteSourcePop(false);
        setSelectedSourceFile(null);
    }

    const formatData = () => {
        const structuredRowData = sourceList?.data?.map((e) => {
            return {
                key: e.sourceId,
                sourceTypeName: e?.sourceSubTypeName || e?.sourceTypeName,
                title: e?.sourceTitle,
                sourceUrl: <div className="preview-pdf-link" onClick={() => handlePreviewPDF(e?.sourceFile)}>Open Doc</div>,
                publicationDate: dayjs(e?.publicationDate).format('DD-MMM-YYYY'),
                actions: (
                    <div className="table-actions-container">
                        {["admin"]?.includes((role_decrypt?.role || "")) && (
                            <div className="action-item">
                                <Tooltip title="Delete Source">
                                    <FaTrashAlt className="action-icon delete-icon" onClick={() => handleDeleteSourceModal(e)} />
                                </Tooltip>
                            </div>
                        )}
                    </div>
                )
            }
        })

        setFormattedTableData({ dataSource: structuredRowData, columns: tableHeaderData })
    }

    const onApply = () => {
        if (selectdCompany) {
            dispatch(SourceListReset())

            let payload = {
                companyName: [selectdCompany],
                fiscalYear: selectedPublicationYear ? [selectedPublicationYear?.toString()] : [],
                searchType: model,
                searchBy: selectedFiltersData?.payloadSearchBy,
                sourceType: selectedSourceDetails?.parents,
                subSourceType: selectedSourceDetails?.children,
                isResearch: true
            }

            dispatch(SourceListAction(payload))
        } else {
            message.error("Please select a Company or Investor")
        }
    }

    const onExport = () => {
        let payload = {
            companyName: selectdCompany ? [selectdCompany] : [],
            fiscalYear: selectedPublicationYear ? [selectedPublicationYear?.toString()] : [],
            searchType: model,
            sourceType: selectedSourceDetails?.parents,
            subSourceType: selectedSourceDetails?.children,
        }

        dispatch(ExportSourceAction(payload));
    }

    const handleShowFiltersSection = () => {
        setShowFilters(!showFilters)
    }

    const handleResize = () => {
        if (window.innerWidth > 1024) {
            setShowFilters(true)
        }
    }

    const handleDeleteSource = () => {
        dispatch(DeleteSourceReset());

        let payload = {
            tokens: [selectedSourceFile?.token]
        }

        dispatch(DeleteSourceAction(payload));
    }

    const DeleteSourcePopup = () => {
        return (
            <div className="delete-source-modal-container">
                <div className="modal-container-title">Are you sure want to delete the source ? </div>

                {!deleteSourceLoading && (
                    <div className="delete-source-button-wrapper">
                        <div className="btn submit" onClick={() => handleDeleteSource()}>Yes</div>
                        <div className="btn cancel" onClick={() => handleCloseDeleteModal()}>No</div>
                    </div>
                )}

                {deleteSourceLoading && (
                    <SpinLoader />
                )}

            </div>
        )
    }

    useEffect(() => {
        handleResize()
        window.addEventListener("resize", handleResize)
        return () => {
            window.removeEventListener("resize", handleResize) //Clean up
        }
    }, [])

    useEffect(() => {
        if (selectdCompany) {
            onApply()
        }
    }, [selectdCompany, selectedPublicationYear])

    useEffect(() => {
        dispatch(SourceListReset())
        dispatch(FetchCompaniesReset())
        dispatch(retrievePortfolioReset())
        
        if (model === "Custom") {
            dispatch(ESGChatFoldersAction())
        } else {
            const payload = {
                searchType: model,
                searchBy: selectedFiltersData?.payloadSearchBy,
                companyName: selectdCompany ? [selectdCompany] : [],
                searchKeyword: searchCompany || "",
            }
            if (model === "Company") {
                dispatch(RetrievePortfolioData(payload))
            } else {
                dispatch(FetchCompaniesAction(payload))
            }
        }
    }, [selectdCompany, searchCompany])

    useEffect(() => {
        if (sourceList?.researchOutput?.length > 0) {
            setSourceOptions([defaultOption, ...sourceList?.researchOutput])
        } 
    }, [sourceList?.researchOutput])
    
    useEffect(() => {
        if (model === "Company" && userPortfolioData?.entities?.length > 0) {
            setCompaniesOptions(userPortfolioData?.entities)
        } else if (companyList?.entities?.length > 0) {
            setCompaniesOptions(companyList?.entities)
        }
    }, [companyList?.entities, userPortfolioData?.entities])

    useEffect(() => {
        if (model === "Company" && userPortfolioData?.fiscalYear) {
            setFiscalYearOptions(userPortfolioData?.fiscalYear)
        } else if (companyList?.fiscalYear) {
            setFiscalYearOptions(companyList?.fiscalYear)
        }
    }, [companyList?.fiscalYear, userPortfolioData?.entities])

    useEffect(() => {
        if (sourceListError?.message) {
            message?.error(sourceListError?.message)
            dispatch(SourceListReset())
        }
    }, [sourceListError?.message])

    useEffect(() => {
        if (exportSourceSuccess) {
            message?.success('Source list exported Successfully');
        }
        if (exportSourceError?.message) {
            message?.error(exportSourceError?.message);
        }
        dispatch(ExportSourceListReset());
    }, [exportSourceSuccess, exportSourceError])

    useEffect(() => {
        formatData()
    }, [sourceList?.data])

    useEffect(() => {
        if (deleteSourceSuccess && deleteSourceData?.message) {
            message?.success(deleteSourceData?.message);
            handleCloseDeleteModal();

            dispatch(DeleteSourceReset());
            onApply();
        }

    }, [deleteSourceSuccess])

    useEffect(() => {
        if (deleteSourceError?.message) {
            message?.error(deleteSourceError?.message);

            dispatch(DeleteSourceReset());
        }

    }, [deleteSourceError?.message])

    return (
        <div className="source-list-main">
            <Breadcrumbs />
            {!unsubscribed && (
                <>
                    <div className="source-list-filters">
                        {showFilters && (
                            <div className="source-list-filters-section">
                                <div className="input-wrapper">
                                    <div className="label">{selectedFiltersData?.companyOption.label} <span className="mandatory-icon">*</span></div>
                                    <div className="input-container">
                                        <Select
                                            className="company-select"
                                            popupClassName='filters-select'
                                            options={chatFolders?.foldersList || companiesOptions || []}
                                            onChange={(e) => onChangeCompany(e)}
                                            onSearch={handleCompanySearch}
                                            value={selectdCompany}
                                            showSearch
                                            allowClear
                                            placeholder={selectedFiltersData?.companyOption.placeholder}
                                            filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                                            notFoundContent={(companyLoading || chatLoading || userPortfolioDataLoading) ? <div className="dropdown-loading"><Spin size="small" /></div> : <div className="dropdown-loading">No Data</div>}
                                        />
                                    </div>
                                </div>
                                <div className="input-wrapper">
                                    <div className="label">Fiscal Year</div>
                                    <div className="input-container">
                                        <Select
                                            className="year-select"
                                            popupClassName='filters-select'
                                            options={fiscalYearOptions || []}
                                            onChange={(e) => onChangePublicationYear(e)}
                                            value={selectedPublicationYear}
                                            showSearch
                                            allowClear
                                            placeholder="Select Year"
                                            filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                                            notFoundContent={(companyLoading || userPortfolioDataLoading) ? <div className="dropdown-loading"><Spin size="small" /></div> : <div className="dropdown-loading">No Data</div>}
                                        />
                                    </div>
                                </div>
                                <div className="input-wrapper">
                                    <div className="label">Disclosure Type</div>
                                    <div className="input-container">
                                        <TreeSelect
                                            className="source-select"
                                            popupClassName='filters-select'
                                            multiple
                                            allowClear
                                            showSearch
                                            treeCheckable
                                            maxTagCount={1}
                                            value={selectdSource}
                                            onChange={onChangeSource}
                                            treeData={sourceOptions || []}
                                            showCheckedStrategy="SHOW_PARENT"
                                            placeholder="Select Disclosure Type"
                                            filterTreeNode={(input, option) => {
                                                return (
                                                    option?.key?.toLowerCase()?.indexOf(input?.toLowerCase()) >= 0 ||
                                                    option?.title?.toLowerCase()?.indexOf(input?.toLowerCase()) >= 0
                                                );
                                            }}
                                            notFoundContent={sourceListLoading ? <div className="dropdown-loading"><Spin size="small" /></div> : <div className="dropdown-loading">No Data</div>}
                                        />
                                    </div>
                                </div>
                                <div className="filters-button-section">
                                    <div className="button-wrapper apply-filter" onClick={() => onApply()}>Apply</div>
                                    <div className="button-wrapper reset-filter" onClick={() => onReset()}>Reset</div>
                                </div>
                                <div className="filters-button-section export-btn">
                                    <div className="button-wrapper apply-filter" onClick={() => onExport()}>
                                        {exportSourceLoading ? (
                                            <LoadingOutlined className="export-loader-icon" spin />
                                        ) : (
                                            <span className="export-icon"><PiExportBold /></span>
                                        )}
                                        Export
                                    </div>
                                </div>
                            </div>
                        )}
                        <ManageFilters showFilters={showFilters} handleShowFiltersSection={handleShowFiltersSection} />
                    </div>
                    <div className="source-list-table-section">
                        <CustomTable
                            page={page}
                            limit={limit}
                            setPage={setPage}
                            setLimit={setLimit}
                            tableData={formattedTableData}
                            isLoading={sourceListLoading}
                        />
                    </div>

                </>
            )
            }
            {unsubscribed && <UnSubscribeUI />}
            {(showPDFPreview) && (
                <PDFPreview setShow={setShowPDFPreview} />
            )}

            {showDeleteSourcePop && (
                <CustomModal modalTitle="Delete Source" showCloseIcon handleClose={handleCloseDeleteModal}>
                    <DeleteSourcePopup />
                </CustomModal>
            )}
        </div >
    )
}

export default SourceListMain
